import React, { useEffect, useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import { RubyLoader } from '../../../components/RubyLoader';
import { RubyToken } from '../../../components/RubyToken';
import { api } from '../../../services/apiService';
import { rubyAiErrorText } from '../../../services/aiControlApi';
import type { AiTabProps } from '../AiControlCenter';
import CreditsTabPanel from '../tabs/CreditsTabPanel';
import {
  includedRubyTokens, shortTierName, sortTiers, withIncludedRubyTokens, type FamilyTier,
} from './catalogModel';
import { formatRubyTokens, formatRubyTokensExact, liveTokensPerCredit } from './units';
import { useAiFamily, useRubyAiCatalog } from './useRubyAi';
import { Badge, Btn, Card, CardHeader, ErrorBanner, Issues, Mono, TextInput } from './ui';

const noop = () => {};

/**
 * Ruby Tokens (mockup board 6). The one thing a tenant buys (D6–D8).
 *
 * Tiers are read from the AI package family record (never constants); each
 * tier's free monthly Ruby Tokens is its `ruby_tokens` meter rule, edited
 * through the existing package PUT (only that rule is replaced — every other
 * entitlement survives). Allowance expires at the next renewal; purchased
 * tokens never expire. The wallet ledger and grants sit below.
 */
const RubyTokensTab: React.FC<AiTabProps> = (props) => {
  const { onHeaderStats, showToast, registry } = props;
  const { family, error, reload } = useAiFamily();
  const { catalog } = useRubyAiCatalog();

  const creditUsd = registry?.config?.creditUnitPriceUsd ?? catalog?.config.creditUnitPriceUsd ?? 0.01;
  const tiers = useMemo(() => sortTiers(family?.tiers ?? []), [family]);
  const topId = tiers[tiers.length - 1]?.id;
  const topTier = tiers.find((t) => t.id === topId);
  const topTierName = topTier ? shortTierName(topTier, true) : family?.family.name ?? 'Packages';

  useEffect(() => {
    onHeaderStats([
      { label: 'Tiers', value: String(tiers.length), tone: 'white' },
      { label: `${formatRubyTokens(liveTokensPerCredit())} Ruby Tokens`, value: `$${creditUsd}`, tone: 'emerald' },
    ]);
  }, [tiers.length, creditUsd, onHeaderStats]);

  /** What each tier unlocks, as increments over the tier below. */
  const unlocks = useMemo(() => {
    if (!catalog) return new Map<string, string>();
    const out = new Map<string, string>();
    let prevActions = new Set<string>();
    let prevVibes = new Set<string>();
    tiers.forEach((t, i) => {
      const f = new Set(t.features ?? []);
      const acts = catalog.actions.filter((a) => f.has(a.featureId));
      const actIds = new Set(acts.map((a) => a.actionId));
      const vibes = catalog.vibes.filter((v) => v.actionIds.some((a) => actIds.has(a)) && (!v.featureId || f.has(v.featureId)));
      const newActs = acts.filter((a) => !prevActions.has(a.actionId)).map((a) => a.label);
      const newVibes = vibes.filter((v) => !prevVibes.has(v.vibeId)).map((v) => v.label);
      const parts: string[] = [];
      if (i === 0) {
        if (newActs.length) parts.push(newActs.join(', '));
        if (newVibes.length) parts.push(`vibes: ${newVibes.join(', ')}`);
      } else {
        if (t.id === topId && acts.length === catalog.actions.length) parts.push('Everything');
        if (newActs.length) parts.push(`+ ${newActs.join(', ')}`);
        if (newVibes.length) parts.push(`+ ${newVibes.join(', ')}`);
      }
      out.set(t.id, parts.join(' · ') || (i === 0 ? 'Nothing yet' : 'Same as the tier below'));
      prevActions = new Set([...prevActions, ...actIds]);
      prevVibes = new Set([...prevVibes, ...vibes.map((v) => v.vibeId)]);
    });
    return out;
  }, [catalog, tiers, topId]);

  return (
    <>
      <Card className="p-4 md:p-5 flex items-start gap-3">
        <RubyToken size={32} drawing title="Ruby Token" />
        <div>
          <h2 className="text-lg font-bold text-stone-900">Ruby Tokens</h2>
          <p className="text-[13px] text-stone-600">
            The one thing a tenant buys. {formatRubyTokensExact(liveTokensPerCredit())} Ruby Tokens = 1 stored credit = ${creditUsd}.
            Storage keeps counting credits; every screen shows Ruby Tokens.
          </p>
        </div>
      </Card>

      {error && !family && <ErrorBanner title="Failed to load the Ruby AI package family" detail={error} />}
      {!family && !error && <RubyLoader variant="section" className="bg-white border border-stone-200 rounded-xl" />}

      {family && (
        <Card className="overflow-hidden">
          <CardHeader
            title={`AI package family — ${family.family.name}`}
            hint={<>Each tier = the features it unlocks + a monthly Ruby Token allowance. Family <Mono>{family.family.id}</Mono> · a tenant holds one tier · {family.family.enrollment ?? 'manual'} enrollment · {family.family.noticeDays ?? 30} days notice.
              {' '}Tiers, names and prices come from Packages › {topTierName}.</>}
          />
          {family.missingPackageIds.length > 0 && (
            <div className="px-4 pt-3"><Issues errors={[`Tier package(s) missing: ${family.missingPackageIds.join(', ')}`]} /></div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead><tr className="bg-stone-50 text-[11px] uppercase tracking-wide text-stone-600">
                <th className="text-left font-semibold px-4 py-2.5">Tier</th>
                <th className="text-left font-semibold px-3 py-2.5">Price / month</th>
                <th className="text-left font-semibold px-3 py-2.5">Free / month</th>
                <th className="text-left font-semibold px-3 py-2.5">Unlocks (actions · vibes)</th>
                <th className="text-left font-semibold px-4 py-2.5">Roll over?</th>
              </tr></thead>
              <tbody>
                {tiers.map((t) => (
                  <TierRow key={t.id} tier={t} isTop={t.id === topId} unlocks={unlocks.get(t.id) ?? '—'}
                    onSaved={reload} showToast={showToast} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 text-[12px] text-stone-600 bg-stone-50 border-t border-stone-200">
            Tenants who already hold the top tier are labelled Enterprise — nothing they have is removed, no price or Stripe item changes.
          </div>
        </Card>
      )}

      <Card className="p-4 md:p-5 space-y-3">
        <h2 className="text-[15px] font-semibold text-stone-900">Order tokens are spent in</h2>
        <div className="flex items-center gap-2.5 flex-wrap text-sm">
          <span className="px-3.5 py-2.5 rounded-lg bg-black text-white">1 · Monthly allowance</span>
          <span className="text-stone-500">→</span>
          <span className="px-3.5 py-2.5 rounded-lg bg-[#FFF1F3] text-[#9F1239] border border-rose-200">2 · Purchased tokens</span>
          <span className="text-stone-500">→</span>
          <span className="px-3.5 py-2.5 rounded-lg bg-stone-100 text-stone-700">3 · Auto top-up, or stop</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[13px]">
          <div className="border border-stone-200 rounded-xl px-3.5 py-3">
            <p className="font-semibold text-stone-900">Auto top-up OFF (default)</p>
            <p className="text-stone-600">When the allowance and any purchased tokens are spent, runs stop until renewal or a top-up.</p>
          </div>
          <div className="border border-stone-200 rounded-xl px-3.5 py-3">
            <p className="font-semibold text-stone-900">Auto top-up ON (tenant opts in)</p>
            <p className="text-stone-600">Buys a pack on the card on file when tokens run out. The merchant setting comes later; the flag exists and defaults off.</p>
          </div>
        </div>
        <p className="text-[12px] text-stone-600">
          The allowance is granted automatically on each paid renewal and <strong>expires at the next one</strong>.
          Purchased tokens <strong>never expire</strong>. Every run holds its action’s maximum charge first, then settles to the real price.
        </p>
      </Card>

      <Card className="p-4 md:p-5 space-y-2">
        <h2 className="text-[15px] font-semibold text-stone-900">What spends tokens</h2>
        <div className="flex flex-wrap gap-2 text-[13px]">
          <Badge tone="ruby" className="!text-[13px] !px-3 !py-1.5">Ruby AI Actions</Badge>
          <Badge tone="ruby" className="!text-[13px] !px-3 !py-1.5">Premium vibes</Badge>
          <Badge tone="stone" className="!text-[13px] !px-3 !py-1.5">Legacy AI calls (per-call pricing, until mapped)</Badge>
        </div>
      </Card>

      <div className="space-y-5">
        <h2 className="text-[15px] font-semibold text-stone-900 pt-1">Wallets, grants and the credit ledger</h2>
        <CreditsTabPanel {...props} onHeaderStats={noop} />
      </div>
    </>
  );
};

const TierRow: React.FC<{
  tier: FamilyTier; isTop: boolean; unlocks: string;
  onSaved: () => Promise<void>; showToast: AiTabProps['showToast'];
}> = ({ tier, isTop, unlocks, onSaved, showToast }) => {
  const included = includedRubyTokens(tier);
  const initial = included == null ? '' : String(included);
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setValue(initial); }, [initial]);

  const n = Number(value);
  const valid = value.trim() !== '' && Number.isInteger(n) && n >= 0;
  const dirty = value !== initial;

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await api.packages.update(tier.id, { entitlements: withIncludedRubyTokens(tier.entitlements, n) });
      showToast(`${shortTierName(tier, isTop)}: ${formatRubyTokens(n)} Ruby Tokens / month`, 'success');
      await onSaved();
    } catch (e) {
      setErr(rubyAiErrorText(e, 'Failed to save the allowance'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-t border-stone-100 align-top">
      <td className="px-4 py-3">
        <p className="font-semibold text-stone-900">{shortTierName(tier, isTop)}</p>
        <p className="text-[12px] text-stone-600">{tier.name} · <Mono>{tier.id}</Mono></p>
        {tier.status && tier.status !== 'active' && <Badge tone="amber">{tier.status}</Badge>}
      </td>
      <td className="px-3 py-3 align-top">
        <div className="font-semibold">{tier.basePrice ? `$${(tier.basePrice / 100).toFixed(2)}` : 'Free'}</div>
        <a href="/packages" className="text-xs text-rose-700 hover:text-rose-800">from Packages</a>
      </td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <div className="w-[230px]">
            <TextInput aria-label={`Free Ruby Tokens per month — ${shortTierName(tier, isTop)}`} inputMode="numeric" value={value}
              placeholder={included === null ? 'Unlimited' : 'No allowance'} suffix="Ruby Tokens"
              onChange={(e) => setValue(e.target.value.replace(/[^\d]/g, ''))} />
          </div>
          {dirty && <Btn kind="primary" className="!px-3" onClick={() => void save()} busy={saving} disabled={!valid}><Save size={16} /> Save</Btn>}
        </div>
        <p className="text-[12px] text-stone-600 mt-1">
          {included === undefined ? 'No meter rule — reads as no allowance, never as a block.'
            : included === null ? 'Unlimited.' : `≈ ${formatRubyTokens(included)} every month`}
        </p>
        {err && <p className="text-[12px] text-rose-700 mt-1">{err}</p>}
      </td>
      <td className="px-3 py-3 text-stone-800">{unlocks}</td>
      <td className="px-4 py-3 text-stone-700">No — expires</td>
    </tr>
  );
};

export default RubyTokensTab;
